"use client";

import React, { useState, useEffect, useRef } from "react";
import { Search, User, Menu, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList, NavigationMenuTrigger, NavigationMenuContent } from "@/components/ui/navigation-menu";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { EcommerceProductsProvider, useEcommerceProducts } from "@/integrations/wordpress/WordPressEcommerceProductsProvider";
import { ProductsSearch, useWPQueryForSearch } from "@/components/shop/ProductsSearch";
import { WP_Query } from "@/integrations/wordpress/wp_query";
function HeaderMenuConsumer({
  isMobile = false
}: {
  isMobile?: boolean;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div data-section-id="4565" className={`flex ${isMobile ? "flex-col gap-4" : "gap-6"}`}>
        {[1, 2, 3, 4].map(i => <div key={i} data-index={i} className="h-5 w-16 animate-pulse rounded-none bg-muted" />)}
      </div>;
  }
  return <NavigationMenu viewport={!isMobile} className={isMobile ? "block max-w-full" : ""}>
      <NavigationMenuList className={isMobile ? "flex-col items-start space-y-4 space-x-0" : ""}>
        {menuItems.map((item, i) => {
        const hasChildren = item.children && item.children.length > 0;
        const commonClasses = `group inline-flex items-center justify-center rounded-none px-4 py-2 text-sm font-semibold tracking-wide transition-colors hover:text-[#D8A7CA] hover:underline decoration-2 underline-offset-8 focus:outline-none disabled:pointer-events-none disabled:opacity-50 ${isMobile ? "text-xl font-sans text-[#333333]" : "bg-transparent text-[#333333] h-9 w-max"}`;
        if (hasChildren) {
          return <NavigationMenuItem key={item.id}>
                <NavigationMenuTrigger className={`${commonClasses} bg-transparent hover:bg-transparent focus:bg-transparent data-[state=open]:bg-transparent hover:text-[#D8A7CA]`}>
                  <Link to={item.href || "#"} data-index={i} className="hover:text-inherit">
                    {item.label}
                  </Link>
                </NavigationMenuTrigger>
                <NavigationMenuContent className={isMobile ? "static bg-transparent border-none shadow-none p-0 mt-2" : "bg-[#FFF7ED] border-[#0D9488]/20 min-w-[200px]"}>
                  <ul className={`flex flex-col gap-1 ${isMobile ? "pl-6" : "p-2"}`}>
                    {item.children.map((child, childIdx) => <li key={child.id}>
                        <NavigationMenuLink asChild>
                          <Link to={child.href || "#"} className={`${commonClasses} ${isMobile ? "text-lg py-1" : "w-full justify-start"}`} data-index={childIdx}>
                            {child.label}
                          </Link>
                        </NavigationMenuLink>
                      </li>)}
                  </ul>
                </NavigationMenuContent>
              </NavigationMenuItem>;
        }
        return <NavigationMenuItem key={item.id}>
              <NavigationMenuLink asChild>
                <Link to={item.href || "#"} className={commonClasses} data-index={i}>
                  {item.label}
                </Link>
              </NavigationMenuLink>
            </NavigationMenuItem>;
      })}
      </NavigationMenuList>
    </NavigationMenu>;
}
function EcommerceProductsConsumer() {
  const {
    products,
    loading,
    error
  } = useEcommerceProducts();
  if (loading) {
    return <p className="p-4 text-xs text-muted-foreground font-sans">Searching...</p>;
  }
  if (error) {
    return <p className="p-4 text-xs text-destructive font-sans">Unable to load results.</p>;
  }
  if (!products.length) {
    return <p className="p-4 text-xs text-muted-foreground font-sans">No names found.</p>;
  }
  const visibleProducts = products.slice(0, 5);
  return <div className="max-h-[60vh] overflow-y-auto py-2">
      {visibleProducts.map((product, i) => <Link key={product.id} to={product.link} className="flex items-center gap-3 border-b border-border/50 p-3 transition-colors hover:bg-[#A7F3D0]/20 last:border-0" data-index={i}>
          {product.featured_image_url && <div className="h-12 w-12 shrink-0 overflow-hidden rounded-none bg-muted">
              <img src={product.featured_image_url} alt={product.title} className="h-full w-full object-cover" />
            </div>}
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-semibold text-[#333333] font-sans">
              {product.title}
            </p>
            <div className="flex items-center gap-2">
              {product.price !== undefined && <span className="text-xs text-[#0D9488] font-sans">
                  Featured
                </span>}
            </div>
          </div>
        </Link>)}
    </div>;
}
function SearchDropdown() {
  const [isOpen, setIsOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [wp_query, hasSearchQuery] = useWPQueryForSearch(searchValue);
  const containerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);
  return <div ref={containerRef} className="relative">
      <Button variant="ghost" size="sm" onClick={() => setIsOpen(!isOpen)} className="text-[#333333] hover:text-[#D8A7CA] font-semibold text-sm tracking-widest uppercase" aria-label="Search">
        <span>Search</span>
      </Button>

      {isOpen && <div className="absolute right-0 top-full mt-2 w-80 origin-top-right rounded-none border border-[#0D9488]/20 bg-[#FFF7ED] shadow-lg ring-1 ring-black/5 focus:outline-none sm:w-96">
          <EcommerceProductsProvider wp_query={wp_query}>
            <div className="p-4 pb-2">
              <ProductsSearch value={searchValue} onChange={setSearchValue} placeholder="Search names..." className="mb-2 border-b border-[#0D9488] bg-[#FFF7ED] rounded-none focus-within:ring-0" />
            </div>
            <Separator className="bg-[#0D9488]/10" />
            {!hasSearchQuery ? <div className="p-4 text-center text-xs text-muted-foreground font-sans">
                Type to explore baby names
              </div> : <EcommerceProductsConsumer />}
          </EcommerceProductsProvider>
        </div>}
    </div>;
}
export default function BrandNavigationHeader() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
            setIsMobileMenuOpen(false);
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <header id="header" className={`sticky top-0 z-50 w-full border-b transition-all duration-300 font-sans ${isScrolled ? "border-[#0D9488]/10 bg-[#FFF7ED]/80 backdrop-blur-md" : "border-transparent bg-[#FFF7ED]"}`}>
      <div className="container mx-auto flex h-16 items-center justify-between px-4 md:px-6">
        <div className="hidden flex-1 md:flex md:justify-start">
          <MenuProvider menu_id="16" className="header-menu">
            <HeaderMenuConsumer />
          </MenuProvider>
        </div>

        <div className="flex flex-1 justify-start md:hidden">
          <Button variant="ghost" size="icon" onClick={() => setIsMobileMenuOpen(true)} className="text-[#333333] hover:text-[#D8A7CA]">
            <Menu className="size-6" />
            <span className="sr-only">Open menu</span>
          </Button>
        </div>

        <div className="flex flex-1 justify-center">
          <Link to="/HomePage" className="flex items-center">
            <span className="text-xl font-bold tracking-tight text-[#0D9488] whitespace-nowrap">
              NamesOfTheWorld.com
            </span>
          </Link>
        </div>

        <div className="flex flex-1 items-center justify-end gap-2 md:gap-4">
          <SearchDropdown />

          <Button variant="ghost" size="icon" asChild className="hidden text-[#333333] hover:text-[#D8A7CA] sm:inline-flex">
            <Link to="#" aria-label="Account">
              <User className="size-5" />
            </Link>
          </Button>
        </div>
      </div>

      {isMobileMenuOpen && <div className="fixed inset-0 z-50 flex flex-col bg-[#FFF7ED] md:hidden">
          <div className="flex h-16 items-center justify-between border-b border-[#0D9488]/10 px-4">
            <Link to="/HomePage" onClick={() => setIsMobileMenuOpen(false)}>
              <span className="text-lg font-bold text-[#0D9488]">
                NamesOfTheWorld.com
              </span>
            </Link>
            <Button variant="ghost" size="icon" onClick={() => setIsMobileMenuOpen(false)} className="text-[#333333]">
              <X className="size-6" />
            </Button>
          </div>
          <div className="flex-1 overflow-y-auto p-6">
            <MenuProvider menu_id="16" className="mobile-menu">
              <HeaderMenuConsumer isMobile />
            </MenuProvider>
            <Separator className="my-6 bg-[#0D9488]/10" />
            <div className="flex flex-col gap-4">
              <Link to="#" className="flex items-center gap-2 text-lg font-semibold text-[#333333]" onClick={() => setIsMobileMenuOpen(false)}>
                <User className="size-5" />
                My Account
              </Link>
            </div>
          </div>
        </div>}
    </header>;
}